
<script setup>
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useUsersStore, useAppModeStore } from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'
import { themeConfig } from '@themeConfig'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import PreferredIcon from '@core/components/PreferredIcon.vue'
import CmsText from '@core/components/CmsText.vue'
import restaurantAdminNavItems from '@/navigation/vertical/restaurantAdmin'

const usersStore = useUsersStore()
const appModeStore = useAppModeStore()

//not sure if this is needed - probs not
usersStore.updateEnvironment()

let appVersion = null
let isRunningServiceWorker = null
let isRunningInStandAlone = null
let isRunningInIOSAppStore = null
let myUser = null;
({ appVersion, isRunningServiceWorker, isRunningInIOSAppStore, isRunningInStandAlone, myUser } = storeToRefs(usersStore))
const { mode: appMode } = storeToRefs(appModeStore)

const buildNumber = ref(import.meta.env.VITE_BUILD_NUMBER)
</script>

<template>
  <div>
    <!-- Mobile Bottom Navigation (only for authenticated users on mobile) -->
    <div
      v-if="$vuetify.display.smAndDown"
      class="mobile-bottom-nav"
    >
      <!-- Member mode nav -->
      <template v-if="appMode !== 'restaurantAdmin'">
        <RouterLink
          to="/discover"
          class="mobile-nav-item"
          aria-label="Discover"
        >
          <div class="mobile-nav-logo">
            <VNodeRenderer :nodes="themeConfig.app.logo" />
          </div>
          <span>{{ $t('nav.discover') }}</span>
        </RouterLink>
        <RouterLink
          to="/bookings"
          class="mobile-nav-item"
          aria-label="Bookings"
        >
          <VIcon size="34">
            mdi-calendar
          </VIcon>
          <span>{{ $t('nav.bookings') }}</span>
        </RouterLink>
        <RouterLink
          to="/ai"
          class="mobile-nav-item"
          aria-label="RM AI"
        >
          <VIcon size="34">
            mdi-square-edit-outline
          </VIcon>
          <span>RM AI</span>
        </RouterLink>
        <RouterLink
          to="/favorites"
          class="mobile-nav-item"
          aria-label="Favorites"
        >
          <VIcon size="34">
            mdi-heart-outline
          </VIcon>
          <span>{{ $t('nav.favorites') }}</span>
        </RouterLink>

        <RouterLink
          v-if="!myUser || myUser.userLevel == 'dummy'"
          to="/login"
          class="mobile-nav-item"
          aria-label="Login"
        >
          <VAvatar
            size="34"
            class="mobile-nav-avatar"
            color="primary"
            variant="tonal"
          >
            <VIcon>mdi-account</VIcon>
          </VAvatar>
          <span>{{ $t('nav.login') }}</span>
        </RouterLink>
        <RouterLink
          v-else
          to="/m"
          class="mobile-nav-item"
          aria-label="Profile"
        >
          <VAvatar
            size="34"
            class="mobile-nav-avatar"
            color="primary"
            variant="tonal"
          >
            <VImg
              v-if="myUser?.profileImageId"
              cover
              :src="mediaUrl(myUser.profileImageId, 'thumb')"
            />
            <div
              v-else-if="myUser?.firstName?.length > 0 || myUser?.lastName?.length > 0"
              class="avatar-fallback"
            >
              <span class="avatar-fallback-text">{{ myUser?.firstName?.[0]?.toUpperCase() }}{{ myUser?.lastName?.[0]?.toUpperCase() }}</span>
            </div>
            <VIcon v-else>
              mdi-account
            </VIcon>
          </VAvatar>
          <span>{{ $t('nav.profile') }}</span>
        </RouterLink>
      </template>

      <!-- Restaurant Admin mode nav (Discover, Bookings, [Admin AI], Fans, Account) -->
      <template v-else>
        <RouterLink
          v-for="item in restaurantAdminNavItems.slice(0, 2)"
          :key="item.to"
          :to="item.to"
          class="mobile-nav-item"
          :aria-label="item.title"
        >
          <div
            v-if="item.useLogo"
            class="mobile-nav-logo"
          >
            <VNodeRenderer :nodes="themeConfig.app.logo" />
          </div>
          <VIcon
            v-else
            size="34"
          >
            {{ item.icon.icon }}
          </VIcon>
          <span>{{ item.title }}</span>
        </RouterLink>

        <!-- Admin Agent (platform admins only) — centered like the member-mode RM AI link -->
        <RouterLink
          v-if="myUser?.userLevel === 'admin'"
          to="/ai/admin"
          class="mobile-nav-item"
          aria-label="RM Team"
        >
          <VIcon size="34">
            mdi-square-edit-outline
          </VIcon>
          <span>RM Team</span>
        </RouterLink>

        <RouterLink
          v-for="item in restaurantAdminNavItems.slice(2)"
          :key="item.to"
          :to="item.to"
          class="mobile-nav-item"
          :aria-label="item.title"
        >
          <div
            v-if="item.useLogo"
            class="mobile-nav-logo"
          >
            <VNodeRenderer :nodes="themeConfig.app.logo" />
          </div>
          <VIcon
            v-else
            size="34"
          >
            {{ item.icon.icon }}
          </VIcon>
          <span>{{ item.title }}</span>
        </RouterLink>
      </template>
    </div>

    <!-- Desktop Footer -->
    <div
      v-if="$vuetify.display.mdAndUp"
      class="d-flex flex-column justify-center h-100"
    >
      <CmsText
        k="footer.memberLine"
        tag="p"
        class="footer-member-line"
      />
      <div class="w-100 d-flex text-xs align-center justify-space-between">
        <a
          href="mailto:contact@reserverm.com"
          rel="noopener noreferrer"
          class="text-primary"
        >contact@reserverm.com</a>
        <VSpacer />
        <RouterLink
          to="/terms"
        >
          {{ $t('nav.terms') }}
        </RouterLink>
        <span class="ml-4"><RouterLink
          to="/privacy"
        >{{ $t('nav.privacy') }}</RouterLink>
        </span>
        <div class="app-version">
          v{{ appVersion }}-b{{ buildNumber }}-{{ (isRunningServiceWorker ? 'sW': 'nSW') }}{{ (isRunningInStandAlone ? 'sT': 'bR') }}{{ isRunningInIOSAppStore ? 'mA' : 'wA' }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.footer-member-line {
  text-align: center;
  font-size: var(--ds-text-sm);
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin: 0 0 2px;
}

.app-version {
  position: absolute;
  bottom: 3px;
  color: #ccc;
  font-size: 0.5rem;
}

.mobile-bottom-nav {
  display: flex;
  justify-content: space-around;
  align-items: center;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 105px;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  z-index: 1000;
  padding: 0 8px;
}

.mobile-nav-item {
  position: relative; /* anchor for the absolutely-hung Beta tag */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 1;
  padding: 8px 4px;
  text-decoration: none;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  transition: color 0.2s ease;
  min-width: 0;
}

.mobile-nav-item span {
  font-size: 10px; /* iOS tab-bar convention — labels subordinate to icons */
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.mobile-nav-item.router-link-active {
  color: rgb(var(--v-theme-primary));
}

.mobile-nav-item:active {
  transform: scale(0.95);
}

.mobile-nav-logo {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-nav-logo :deep(svg) {
  width: 34px;
  height: 34px;
}

.mobile-nav-avatar {
  border: 2px solid transparent;
}

.router-link-active .mobile-nav-avatar {
  border-color: rgb(var(--v-theme-primary));
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-fallback-text {
  font-size: 12px;
  font-weight: 500;
}

.beta-tag {
  /* Hangs below the item without adding to its height, so the RM AI icon
     stays on the same line as the other tab-bar icons */
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 7px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: var(--ds-brand-primary-darker);
  line-height: 1;
  margin-top: -2px;
}
</style>
